import { CheckCircle, Download, CreditCard, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChecklistItem } from "@/components/ui/checklist-item";
import PhotoWithSidebar from "../photo-with-sidebar";
import { handleDownload } from "../utils";
import {
  STEP_CONTAINER_CLASS,
  CTA_PRIMARY_BUTTON_CLASS,
  CTA_PRIMARY_COLUMN_CLASS,
  CTA_ROW_CLASS,
  CTA_SECONDARY_BUTTON_CLASS,
  CTA_SECONDARY_COLUMN_CLASS,
} from "./cta-classes";
import type { ValidationResult } from "@/types/validation";

const FINAL_CHECKS = [
  "The photo looks like you",
  "Background appears plain and uniform",
  "Both eyes are clearly visible",
  "Expression is neutral",
] as const;

interface SuccessStepProps {
  documentLabel: string;
  result: ValidationResult;
  onUploadNew: () => void;
}

export default function SuccessStep({ documentLabel, result, onUploadNew }: SuccessStepProps) {
  return (
    <div className={STEP_CONTAINER_CLASS}>
      <div className="mb-8">
        <PhotoWithSidebar
          imageUrl={result.imageUrl}
          imageAlt={`Validated ${documentLabel} photo`}
          highlight="approved"
          sidebar={
            <div className="flex flex-col h-full">
              <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-3">
                Final Checks
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                Please confirm these details before proceeding.
              </p>
              <ul className="space-y-2.5">
                {FINAL_CHECKS.map((label) => (
                  <li key={label}>
                    <ChecklistItem icon={CheckCircle} variant="default" size="sm">
                      {label}
                    </ChecklistItem>
                  </li>
                ))}
              </ul>
            </div>
          }
        />
      </div>

      <div className="space-y-4">
        <div className={CTA_ROW_CLASS}>
          <div className={CTA_PRIMARY_COLUMN_CLASS}>
            <form
              className="w-full contents"
              action={`/api/stripe/create-checkout-session?orderId=${result?.orderId || ''}&bgRemoved=false`}
              method="POST"
            >
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className={CTA_PRIMARY_BUTTON_CLASS}
                disabled={!result?.orderId}
              >
                <CreditCard className="w-4 h-4" />
                Order Printed Photos - €9.99
              </Button>
            </form>
          </div>

          <div className={CTA_SECONDARY_COLUMN_CLASS}>
            <Button
              variant="outline"
              size="lg"
              className={CTA_SECONDARY_BUTTON_CLASS}
              onClick={() => handleDownload(result.sheetUrl || result.imageUrl)}
              disabled={!result.imageUrl}
            >
              <Download className="w-4 h-4" />
              Free Digital Download
            </Button>
          </div>
        </div>

        <div className="pt-4 border-t border-border">
          <Button
            variant="ghost"
            size="lg"
            className="w-full text-muted-foreground hover:text-foreground"
            onClick={onUploadNew}
          >
            <Upload className="w-4 h-4" />
            Upload New Photo
          </Button>
        </div>
      </div>
    </div>
  );
}
